<template>
	<div class="SpectrogramPlayer" :class="classes" dir="ltr" @keydown.prevent.stop.capture.space="togglePlay" tabindex="0">
		<div class="SpectrogramPlayer-frame" aria-hidden="true" @click.prevent.capture="!annotation ? togglePlay() : false">
			<img
				class="SpectrogramPlayer-poster"
				role="presentation"
				:alt="'ML' + assetId + ' visualization'"
				v-if="!isActive && !autoplay && !stillProcessing"
				:src="poster"
				:class="{ offset: posterOffset }"
				@click="togglePlay"
				@error="mediaMissing = true"
			/>
			<div v-if="showLoading" class="LoadingAnimation">
				<CloIcon symbol="spinner" />
			</div>
			<div v-if="stillProcessing && !mediaMissing" class="SpectrogramPlayer-errorMessage">
				<CloIcon symbol="timer" />
				{{ messages.errorNoSpectrogram }}
			</div>
			<div v-if="mediaMissing" class="SpectrogramPlayer-errorMessage">
				<CloIcon symbol="timer" />
				{{ messages.errorNoAudio }}
			</div>
			<div class="SpectrogramPlayer-slider" ref="slider" v-if="showSpectrogramPlayer">
				<div class="SpectrogramPlayer-spectContainer" ref="spectContainer" :style="{ width: spectWidth + 'px' }">
					<img
						class="SpectrogramPlayer-spect"
						role="presentation"
						:alt="'ML' + assetId + ' visualization'"
						v-show="isActive"
						:src="spectrogram"
						v-on:load="spectLoaded"
						v-on:mouseenter="mouseEnterSpectrogramPlayer"
						v-on:mouseleave="mouseLeaveSpectrogramPlayer"
						v-on:mousemove="positionCursorSelect"
					/>
					<CloAnnotations
						v-if="annotation"
						:enableAnnotating="enableAnnotating"
						:initialAnnotations="initialAnnotations"
						:activeAnnotation="activeAnnotation"
						:alternateHandles="alternateHandles"
						v-on:annotation-changed="saveAnnotation"
					/>
				</div>

				<div class="SpectrogramPlayer-xAxis" :style="{ width: totalWidth + 'px' }" aria-hidden="true">
					<span
						class="SpectrogramPlayer-tick"
						v-for="(tick, index) in ticks"
						:key="tick.time"
						:style="{ left: tick.position + 'px' }"
					>
						<span v-if="!(index % 4)">{{ tick.label }}</span>
					</span>
				</div>
				<div class="SpectrogramPlayer-startCursor" v-show="showStartCursor" :style="{ left: startCursorPosition + 'px' }"></div>
			</div>
			<div class="SpectrogramPlayer-yAxis" v-show="isActive" aria-hidden="true">
				<span class="SpectrogramPlayer-tick" v-for="tick in freqs" :key="tick.time" :style="{ top: tick.position + 'px' }">
					{{ tick.label }}
				</span>
				<span class="SpectrogramPlayer-khz">kHz</span>
			</div>
			<div
				class="SpectrogramPlayer-cursor"
				aria-hidden="true"
				v-show="isActive && !stillProcessing"
				:style="{ left: cursorPosition + 'px' }"
			></div>
			<div
				class="SpectrogramPlayer-cursorSelect"
				aria-hidden="true"
				v-show="mouseOverSpectrogramPlayer"
				:style="{ left: cursorSelectPosition + 'px' }"
			></div>
		</div>
		<div class="SpectrogramPlayer-timeline" v-if="timeline" aria-hidden="true">
			<img
				class="SpectrogramPlayer-timelineImage"
				v-if="showSpectrogramPlayer"
				:src="spectrogram"
				role="presentation"
				:alt="'ML' + assetId + ' visualization'"
				:style="{ width: timelineImageWidth + 'px' }"
			/>
			<div class="SpectrogramPlayer-timelineBefore" v-if="showSpectrogramPlayer" :style="{ width: timelineLeft + 'px' }"></div>
			<div
				class="SpectrogramPlayer-timelineNow"
				v-if="showSpectrogramPlayer"
				:style="{
					left: timelineLeft + 'px',
					width: timelineWidth + 'px',
				}"
			></div>
			<div class="SpectrogramPlayer-timelineAfter" v-if="showSpectrogramPlayer" :style="{ left: timelineRight + 'px' }"></div>
		</div>
		<audio
			v-bind:aria-label="'ML' + assetId"
			:src="media"
			ref="audio"
			@timeupdate="timeUpdated"
			@loadedmetadata="loadedMetadata"
			@playing="isPlaying"
			@pause="isPaused"
			@canplaythrough.once="canPlayThrough"
			preload="metadata"
			@error="mediaMissing = true"
		></audio>
		<!--mediaControls component starts from here-->
		<CloMediaControls
			:playing="playing"
			:messages="{
				seek: 'seek',
				play: 'play',
				fullscreen: 'fullscreen',
				volume: 'volume',
				mute: 'mute',
			}"
			:showVolume="true"
			:citationUrl="citationUrl"
			:citationName="citationName"
			:title="title"
			:user="user"
			:date="date"
			:location="location"
			v-on:togglePlay="togglePlay"
			:assetId="assetId"
			:progressPercent="progressPercent"
			v-on:setPosition="setPosition"
			v-on:setVolume="setVolume"
			v-on:setTime="setTime"
			v-on:toggleVolume="toggleVolume"
			v-on:set-zoom="setZoom"
			:duration="duration"
			:currentTime="time"
			:timeline="timeline"
			:zoom="zoom"
		/>
	</div>
</template>

<script>
import axios from 'axios';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';
import CloMediaControls from '@/media/CloMediaControls/CloMediaControls';
import CloIcon from '@/icons/CloIcon/CloIcon';
import CloAnnotations from '@/media/CloAnnotations/CloAnnotations';

export default {
	components: {
		CloLoadingAnimation,
		CloMediaControls,
		CloIcon,
		CloAnnotations,
	},

	data() {
		return {
			annotations: [],
			isActive: false,
			showSpectrogramPlayer: false,
			readyToPlay: false,
			spectHasLoaded: false,
			playing: false,
			position: 0,
			posterOffset: false,
			time: 0,
			duration: 0,
			durationText: '',
			lastUpdate: 0,
			totalWidth: 0,
			frameWidth: 0,
			playerWidth: 0,
			ticks: [],
			freqs: [2, 4, 6, 8, 10],
			fixedCursor: true,
			frameOffset: 0,
			cursorPosition: 0,
			progressTime: 0,
			progressWidth: 0,
			progressPercent: 0.0,
			mouseOverSpectrogramPlayer: false,
			cursorSelectPosition: 0,
			timelineLeft: 0,
			timelineWidth: 0,
			timelineRight: 0,
			timelineImageWidth: 0,
			pixelsPerSecond: 0,
			spectWidth: 0,
			zoomLevel: 1,
			speedSlider: 100,
			speed: 1,
			startCursorPosition: 0,
			showStartCursor: false,
			volumeSlider: 1,
			volumeToRestore: 1,
			showVolume: false,
			showLoading: false,
			stillProcessing: false,
			mediaMissing: false,
			specimenBaseUrl: 'https://macaulaylibrary.org/asset/',
		};
	},
	props: {
		/**
		 * ID of the media provided by the database
		 */
		assetId: {}, // can be Number or String
		/**
		 * URL for the citation
		 */
		citationUrl: {
			default: false,
		},
		/**
		 * Name of the citation
		 */
		citationName: {
			default: null,
			type: String,
		},
		advanced: {
			default: false,
			type: Boolean,
		},
		timeline: {
			default: false,
			type: Boolean,
		},
		zoom: {
			default: false,
			type: Boolean,
		},
		assetRestricted: {
			default: false,
			type: Boolean,
		},
		/**
		 * Name of the user
		 */
		user: {
			default: null,
			type: String,
		},
		/**
		 * Location of the user
		 */
		location: {
			default: null,
			type: String,
		},
		/**
		 * Date added or created
		 */
		date: {
			default: null,
			type: String,
		},
		/**
		 * Title of the component
		 */
		title: {
			default: null,
			type: String,
		},
		/**
		 * Indicates whether to play media automatically or not
		 */
		autoplay: {
			default: false,
			type: Boolean,
		},
		/**
		 * Size changes the height (and playback speed) can be 'sm' or 'xs'
		 */
		size: {
			default: '',
			type: String,
		},
		mediaDownloadUrl: {
			default: 'https://cdn.download.ams.birds.cornell.edu/api/v1/asset/',
			type: String,
		},
		/**
		 * Enables ability to view, draw, and edit boxes
		 */
		annotation: {
			type: Boolean,
			default: false,
		},
		/**
		 * Enables drawing and editing boxes
		 */
		enableAnnotating: {
			type: Boolean,
			default: false,
		},
		/**
		 * The initial set of annotations to render
		 */
		initialAnnotations: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * Sets label, color, and any other data for the current annotation being drawn
		 */
		activeAnnotation: {
			type: Object,
			default() {
				return {};
			},
		},
		/**
		 * Annotations don't show white boxes for handles
		 */
		alternateHandles: {
			type: Boolean,
			default: false,
		},
		/**
		 * Skips to any time (in seconds)
		 */
		skipToTime: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to skip back 2s
		 */
		skipBackIncrement: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to skip ahead 2s
		 */
		skipAheadIncrement: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to play and pause
		 */
		togglePlayIncrement: {
			type: Number,
			default: 0,
		},
	},
	watch: {
		skipToTime(newTime) {
			if (this.isActive) {
				this.time = newTime;
				this.$refs.audio.currentTime = newTime;
				this.setTimeAndPosition();
				this.updateProgress();
			}
		},
		skipBackIncrement() {
			if (this.isActive) {
				const currentTime = this.time;
				const newTime = currentTime - 2 > 0 ? currentTime - 2 : 0;
				this.animateSkip(currentTime, newTime);
			}
		},
		skipAheadIncrement() {
			if (this.isActive) {
				const currentTime = this.time;
				const newTime = currentTime + 2 < this.duration ? currentTime + 2 : this.duration;
				this.animateSkip(currentTime, newTime);
			}
		},
		togglePlayIncrement() {
			this.togglePlay();
		},
	},
	computed: {
		poster() {
			return this.mediaDownloadUrl + this.assetId + '/poster';
		},
		media() {
			return this.mediaDownloadUrl + this.assetId + '/audio';
		},
		spectrogram() {
			return this.mediaDownloadUrl + this.assetId + '/spectrogram_small';
		},
		specimenUrl() {
			// if citationUrl is false, the asset has no parent and specimenUrl is restricted
			// so link/branding should be hidden
			if (this.citationUrl || this.assetRestricted) {
				return false;
			} else {
				return this.specimenBaseUrl + this.assetId;
			}
		},
		frameHeight() {
			switch (this.size) {
				case 'compact':
				case 'sm':
					return 168 + 16;
					break;
				case 'xs':
					return 110 + 16;
					break;
				default:
					return 220 + 16;
			}
		},
		originalPixelsPerSecond() {
			switch (this.size) {
				case 'compact':
				case 'sm':
					return 190.9;
					break;
				case 'xs':
					return 125;
					break;
				default:
					return 250;
			}
		},
		messages() {
			return {
				mute: 'mute',
				volume: 'volume',
				seek: 'seek',
				play: 'play',
				zoom: 'zoom',
				errorNoSpectrogram: 'Sorry, there was an error loading this file. Please check later.',
				errorNoAudio: 'We are still processing this file. Please check later.',
			};
		},
		classes() {
			const classes = [];
			const prefix = 'SpectrogramPlayer--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.annotation) {
				classes.push(prefix + 'annotation');
			}
			return classes.join(' ');
		},
	},
	beforeMount() {
		if (this.size === 'compact') {
			this.size == 'sm';
		}
	},
	mounted() {
		// listeners for resize, togglePlay, pause, destroy
		document.addEventListener('lichen:resize', this.handleResize);
		document.addEventListener('lichen:spect-togglePlay', this.handleTogglePlayEvent);
		document.addEventListener('lichen:spect-pause', this.pause);
		document.addEventListener('lichen:spect-destroy', this.destroyPlayer);
	},
	beforeDestroy() {
		this.removeListeners();
	},
	methods: {
		removeListeners() {
			document.removeEventListener('lichen:resize', this.handleResize);
			document.removeEventListener('lichen:spect-togglePlay', this.handleTogglePlayEvent);
			document.removeEventListener('lichen:spect-pause', this.pause);
			document.removeEventListener('lichen:spect-destroy', this.removeListeners);
		},
		destroyPlayer(e) {
			if (e.detail.assetId === parseInt(this.assetId)) {
				if (this.playing) {
					this.pause();
				}
				this.isActive = false;
			}
			this.removeListeners();
		},
		handleTogglePlayEvent(e) {
			if (e.detail.assetId === parseInt(this.assetId)) {
				this.togglePlay();
			}
		},
		setVolume(level) {
			this.$refs.audio.volume = level;
			this.volumeToRestore = level;
		},
		togglePlay() {
			if (this.isActive) {
				if (this.$refs.audio) {
					if (this.playing) {
						this.$refs.audio.pause();
					} else {
						if (this.showStartCursor && this.advanced) {
							this.startFromCursor();
						}
						this.$refs.audio.play();
					}
				}
			} else {
				// don't let play until we have metadata
				if (this.duration > 0) {
					//console.log('try to play')
					this.fakePlay();
					this.showLoading = true;
					this.showSpectrogramPlayer = true;
				}
			}
		},
		isPlaying() {
			this.playing = true;
			this.runTimer(true);
		},
		fakePlay() {
			// for safari and firefox, play, then pause right away
			if (
				(navigator.userAgent.indexOf('Safari') !== -1 || navigator.userAgent.indexOf('Firefox') !== -1) &&
				navigator.userAgent.indexOf('Chrome') === -1
			) {
				if (this.$refs.audio) {
					let promise = this.$refs.audio.play();
					if (promise !== undefined) {
						promise
							.catch((error) => {
								// no play
							})
							.then(() => {
								// yes play
								this.$refs.audio.pause();
								this.$refs.audio.currentTime = 0;
							});
					}
				}
			}
		},
		isPaused() {
			this.playing = false;
		},
		pause() {
			if (this.$refs.audio) {
				this.$refs.audio.pause();
			}
			this.playing = false;
		},
		// the loop
		runTimer(shouldAdvanceFrame) {
			const loop = () => {
				// measure how long it's been since the last loop
				const timeSinceLastUpdate = performance.now() - this.lastUpdate;
				//console.log('timeSinceLastUpdate', timeSinceLastUpdate);
				//console.log('perf.now', performance.now());
				this.time += timeSinceLastUpdate / 1000;
				this.setTimeAndPosition();
				this.updateProgress();
				this.lastUpdate = performance.now();
				if (this.playing) {
					// recursively call itself
					requestAnimationFrame(loop);
				}
			};
			this.lastUpdate = performance.now();
			requestAnimationFrame(loop);
		},
		// the animation done in each loop
		setTimeAndPosition() {
			/*const position = this.time * this.pixelsPerSecond;
			if (this.fixedCursor) {
				this.position = (this.frameWidth / 2) - position;
			} else {
				if (this.cursorPosition + 50 - 18 < this.frameWidth) {
					this.cursorPosition = position - this.frameOffset + 18;
				} else {
					const percentage = this.time / this.duration;
					const offset = percentage * this.totalWidth - 50 ;
					this.frameOffset = offset;
					this.position = this.frameOffset * -1;
					this.cursorPosition = 50 + 18;
				}
			}*/
			this.position = this.frameWidth / 2 - this.time * this.pixelsPerSecond;
			// this might be faster than letting vue do it?
			const slider = this.$refs.slider;
			if (slider) {
				this.$refs.slider.style.transform = 'translateX(' + this.position + 'px)';
			}
		},
		updateTimelineWidth() {
			// image width
			this.timelineImageWidth = this.playerWidth;
			const maxDuration = 65500.0 / this.originalPixelsPerSecond;
			if (this.duration > maxDuration) {
				this.timelineImageWidth = (maxDuration / this.duration) * this.playerWidth;
			}
			// current window width
			this.timelineWidth = Math.pow(this.playerWidth, 2) / this.totalWidth;
		},
		// progress bar and timeline animation
		updateProgress() {
			this.progressPercent = (100 * this.time) / this.duration;
			if (this.timeline) {
				this.progressWidth = (this.time / this.duration) * this.playerWidth;
				if (this.fixedCursor) {
					this.timelineLeft = this.progressWidth - this.timelineWidth / 2;
				} else {
					this.timelineLeft = this.frameWidth * (-this.position / this.totalWidth); // playerWidth
				}
				this.timelineRight = this.timelineLeft + this.timelineWidth;
			}
		},
		// time values should be formatted as 0:00
		formatTime(time) {
			return Math.floor(time / 60) + ':' + ('0' + Math.floor(time % 60)).slice(-2);
		},
		// the audio player sends an event with currentTime every 1/4 second or so, and we resync
		timeUpdated(e) {
			/*
			const difference = this.time - this.$refs.audio.currentTime;
			const timeSinceLastUpdate = this.time - this.lastUpdate;
			//console.log('difference', difference);
			//console.log('time since last update', timeSinceLastUpdate);
			if (Math.abs(difference) < 0.1 && difference !== 0 && timeSinceLastUpdate !== 0) {
				this.pixelsPerSecond += -this.pixelsPerSecond * (difference / timeSinceLastUpdate);
			}
			*/
			//console.log('pixPerSecond', this.pixelsPerSecond);
			if (this.$refs.audio) {
				this.time = this.$refs.audio.currentTime;
			}
		},
		// <audio preload="metadata"/> means we get an event when the player gets the file's metadata
		// we only need duration, and use this value to set other values, and sometimes trigger autoplay
		loadedMetadata(e) {
			this.playerWidth = this.$el.clientWidth;
			this.frameWidth = this.playerWidth - 16;
			if (this.$refs.audio) {
				const duration = this.$refs.audio.duration;
				this.duration = duration;
				this.durationText = this.formatTime(duration);
				this.$emit('spectrogram-duration', duration);
			}
			this.cursorPosition = this.playerWidth / 2;
			//this.totalWidth = duration * this.pixelsPerSecond;
			this.renderFrequencies();
			if (this.autoplay) {
				this.showLoading = true;
				this.showSpectrogramPlayer = true;
				this.fakePlay();
			}
			if (this.stillProcessing) {
				this.isActive = true;
				this.showLoading = false;
			}
			if (this.annotation) {
				this.showLoading = true;
				this.showSpectrogramPlayer = true;
			}
		},
		// the y axis
		renderFrequencies() {
			const freqs = this.freqs;
			this.freqs = freqs.reverse().map((freq, index) => {
				return {
					label: freq,
					position: index * ((this.frameHeight - 16) / freqs.length) + 16,
				};
			});
		},
		// event when the full spectrogram image is loaded (don't play until it's loaded)
		spectLoaded(e, img) {
			//console.log('spect loaded');
			this.spectHasLoaded = true;
			this.isActive = true;
			this.originalSpectrogramPlayerWidth = e.target.width;
			this.showLoading = false;
			this.setupSpectrogramPlayer();
			this.$nextTick(() => {
				// can't set slider position if it's not there yet
				//this.setTimeAndPosition();
				if (!this.annotation) {
					this.startPlayIfReady();
				} else {
					this.currentTime = 0;
					this.setTimeAndPosition();
					//this.$refs.audio.currentTime = 0;
				}
			});
		},
		startPlayIfReady() {
			//console.log('can we play?', this.readyToPlay, this.spectHasLoaded);
			if (this.readyToPlay && this.spectHasLoaded && this.$refs.audio) {
				//console.log('yes, play it sam!')
				let promise = this.$refs.audio.play();
				if (promise !== undefined) {
					promise
						.catch((error) => {
							// no play
						})
						.then(() => {
							// yes play
						});
				}

				this.showLoading = false;

				let event;
				const detail = {
					assetId: parseInt(this.assetId),
					origin: window.location.origin,
					pathname: window.location.pathname,
				};
				if (typeof window.CustomEvent === 'function') {
					event = new CustomEvent('lichen:audio-play', {
						detail: detail,
					});
				} else {
					event = document.createEvent('CustomEvent');
					event.initCustomEvent('lichen:audio-play', true, true, detail);
				}
				document.dispatchEvent(event);
			}
		},
		canPlayThrough() {
			//console.log('can play through');
			this.readyToPlay = true;
			this.startPlayIfReady();
		},
		// the x-axis, and anything that depends on the player width
		setupSpectrogramPlayer() {
			// speed
			this.pixelsPerSecond = this.originalPixelsPerSecond * this.zoomLevel;

			// ticks
			this.ticks = [];
			for (let x = 0; x <= this.duration; x = x + 0.25) {
				this.ticks.push({
					time: x,
					label: this.formatTime(x),
					position: x * this.pixelsPerSecond,
				});
			}

			// spect
			const maxDuration = 65500.0 / this.originalPixelsPerSecond;
			if (this.duration > maxDuration) {
				this.totalWidth = this.originalSpectrogramPlayerWidth * this.zoomLevel;
			} else {
				this.totalWidth = this.duration * this.pixelsPerSecond;
			}
			this.spectWidth = this.totalWidth;

			// timeline
			this.updateTimelineWidth();

			// cursor (needed if initialized when hidden)
			if (this.fixedCursor) {
				this.cursorPosition = this.playerWidth / 2;
			}
		},
		// an event when window is resized
		handleResize(e) {
			requestAnimationFrame(() => {
				this.playerWidth = this.$el.clientWidth;
				this.frameWidth = this.playerWidth - 16;
				this.updateTimelineWidth();
				this.setTimeAndPosition();
				if (this.fixedCursor) {
					this.cursorPosition = this.playerWidth / 2;
				}
			});
		},
		toggleScroll() {
			this.fixedCursor = !this.fixedCursor;
			if (this.fixedCursor) {
				this.cursorPosition = this.playerWidth / 2;
			}
		},
		toggleTimeline() {
			this.timeline = !this.timeline;
		},
		// clicking on the "seek" slider, or adjusting the slider (end of drag)
		setTime(value) {
			requestAnimationFrame(() => {
				this.time = parseFloat(value);
				this.$refs.audio.currentTime = this.time;
				this.setTimeAndPosition();
				this.updateProgress();
			});
		},
		// adjusting the seek slider (during drag)
		setPosition(value) {
			requestAnimationFrame(() => {
				this.time = parseFloat(value);
				if (!this.fixedCursor) {
					// force condition to be false in setTimeAndPosition()
					this.cursorPosition = this.frameWidth;
				}
				this.setTimeAndPosition();
				this.updateProgress();
			});
		},
		setZoom(zoomSlider) {
			requestAnimationFrame(() => {
				zoomSlider = parseInt(zoomSlider);
				if (zoomSlider < 50) {
					this.zoomLevel = Math.sqrt(zoomSlider / 50);
				} else {
					this.zoomLevel = (2 * zoomSlider) / 100;
				}
				if (this.isActive) {
					this.setupSpectrogramPlayer();
					this.setTimeAndPosition();
					this.updateProgress();
				}
			});
		},
		setSpeed(e) {
			this.speed = Math.sqrt(e.target.value / 100);
			if (this.isActive) {
				this.$refs.audio.playbackRate = this.speed;
				this.setupSpectrogramPlayer();
			}
		},
		toggleVolume() {
			if (this.volumeSlider == 0) {
				if (this.volumeToRestore == 0) {
					this.volumeSlider = 1;
				} else {
					this.volumeSlider = this.volumeToRestore;
				}
			} else {
				this.volumeSlider = 0;
			}
			this.setVolume();
		},
		// dropping cursors
		clickOnSpectrogramPlayer(e) {
			if (this.advanced) {
				if (this.showStartCursor) {
					this.showStartCursor = false;
				} else {
					// TODO: why 11?
					const rect = this.$el.getBoundingClientRect();
					const clickPositionInSlider = e.clientX - rect.left + window.pageXOffset - this.position - 11;
					this.startCursorPosition = clickPositionInSlider;
					if (!this.showStartCursor) {
						this.showStartCursor = true;
					}
				}
			}
			if (this.annotation) {
			} else {
				console.log('play');
				this.togglePlay();
			}
		},
		mouseEnterSpectrogramPlayer(e) {
			if (this.advanced) {
				this.mouseOverSpectrogramPlayer = true;
			}
		},
		mouseLeaveSpectrogramPlayer(e) {
			if (this.advanced) {
				this.mouseOverSpectrogramPlayer = false;
			}
		},
		// show cursor on mouse hover
		positionCursorSelect(e) {
			if (this.advanced) {
				requestAnimationFrame(() => {
					this.mouseOverSpectrogramPlayer = true;
					const rect = this.$el.getBoundingClientRect();
					this.cursorSelectPosition = e.clientX - rect.left + window.pageXOffset - 2;
				});
			}
		},
		saveAnnotation(payload) {
			this.$emit('annotation-changed', payload);
		},
		startFromCursor() {
			const percentage = this.startCursorPosition / this.totalWidth;
			this.time = this.duration * percentage;
			this.$refs.audio.currentTime = this.time;
		},
		animateSkip(oldValue, newValue) {
			const wasPlaying = this.playing;
			if (wasPlaying) {
				this.pause();
			}
			const time = 0.3; // seconds
			let currentTime = 0;
			const tick = () => {
				currentTime += 1 / 60;
				const p = currentTime / time;
				// ease in and out
				const t = -0.5 * (Math.cos(Math.PI * p) - 1);
				if (p < 1) {
					requestAnimationFrame(tick);
					this.$refs.audio.currentTime = oldValue + (newValue - oldValue) * t;
				} else {
					// scroll is done
					this.$refs.audio.currentTime = newValue;
				}
				this.setTimeAndPosition();
				this.updateProgress();
			};
			tick();
			if (wasPlaying) {
				this.togglePlay();
			}
		},
	},
};
</script>

<style lang="scss">
.SpectrogramPlayer {
	/*@noflip*/
	text-align: left;
	overflow: hidden;
	background-color: $color-white;
	user-select: none;

	.SpectrogramPlayer-frame {
		position: relative;
		height: 236px;
		overflow: hidden;
		border: 1px solid $color-neutral-1;
		border-bottom: 0;
		cursor: pointer;
	}

	// image shown before playing
	.SpectrogramPlayer-poster {
		height: 220px;
		width: auto;
		position: absolute;
		top: 16px;
		cursor: pointer;
	}

	// the slider is what animates
	.SpectrogramPlayer-slider {
		// not sure why this isn't 16 or 18 or 0
		/*@noflip*/
		margin-left: 9px;
		will-change: transform;
		position: absolute;
	}
	.SpectrogramPlayer-spectContainer {
		position: relative;
		margin-top: 16px;
	}

	// the full spectrogram image
	.SpectrogramPlayer-spect {
		height: 220px;
		width: 100%;
		// for non-advanced, clicking on spect toggles play
		cursor: pointer;
	}

	// the red cursor
	.SpectrogramPlayer-cursor {
		position: absolute;
		top: 0;
		/*@noflip*/
		left: 50%;
		height: 100%;
		width: 1px;
		background-color: red;
		pointer-events: none;
	}

	// the cursor shown where the mouse cursor is
	.SpectrogramPlayer-cursorSelect {
		position: absolute;
		top: 0;
		width: 1px;
		background-color: transparent;
		height: 100%;
		// hide on touch devices
		[data-whatinput='mouse'] &,
		[data-whatinput='keyboard'] & {
			background-color: rgba(128, 128, 128, 0.4);
		}
	}

	// blinking cursor
	@keyframes blink {
		from,
		59.9% {
			opacity: 1;
		}
		60%,
		to {
			opacity: 0;
		}
	}
	.SpectrogramPlayer-startCursor {
		position: absolute;
		top: 0;
		width: 1px;
		background-color: $color-neutral-3;
		height: 100%;
		opacity: 1;
		animation: blink 400ms alternate infinite;
	}

	// the x axis
	.SpectrogramPlayer-xAxis {
		position: absolute;
		top: 0;
		/*@noflip*/
		left: 0;
		height: 16px;
		background-color: $color-white;
		color: $color-neutral-3;
		@include text(2);
		border-bottom: 1px solid $color-neutral-1;
		.SpectrogramPlayer-tick {
			position: absolute;
			top: 0px;
			height: 3px;
			/*@noflip*/
			border-left: 1px solid $color-neutral-2;
			&:nth-child(2n + 1) {
				height: 6px;
				border-color: $color-neutral-2;
			}
			&:nth-child(4n + 1) {
				height: 16px;
				border-color: $color-neutral-2;
				background-color: $color-white;
				/*@noflip*/
				padding-left: 3px;
			}
		}
	}

	// the y axis
	.SpectrogramPlayer-yAxis {
		position: absolute;
		top: 0;
		/*@noflip*/
		left: 0;
		width: 16px;
		height: 100%;
		background-color: $color-white;
		color: $color-neutral-3;
		font-size: $text-size-xs;
		.SpectrogramPlayer-tick {
			position: absolute;
			/*@noflip*/
			left: 0px;
			width: 16px;
			border-top: 1px solid $color-neutral-2;
			padding-top: 2px;
			/*@noflip*/
			padding-left: 2px;
		}
		.SpectrogramPlayer-khz {
			position: absolute;
			bottom: 0;
			/*@noflip*/
			left: 1px;
			font-size: 8px;
			color: $color-neutral-3;
		}
	}

	// the timeline
	.SpectrogramPlayer-timeline {
		position: relative;
		height: 32px;
		overflow: hidden;
		background-color: $color-neutral-0;
		//border-top: 1px solid $color-neutral-2;
		.SpectrogramPlayer-timelineImage {
			height: 32px;
			max-width: 100%;
			margin: 0;
		}
		.SpectrogramPlayer-timelineBefore,
		.SpectrogramPlayer-timelineNow,
		.SpectrogramPlayer-timelineAfter {
			position: absolute;
			top: 0;
			bottom: 0;
			/*@noflip*/
			right: auto;
			/*@noflip*/
			left: 0;
			background-color: rgba(0, 0, 0, 0.1);
			will-change: left;
			box-shadow: inset 0 3px 3px -3px $color-overlay-black;
		}
		.SpectrogramPlayer-timelineNow {
			background-color: rgba(255, 255, 255, 0.3);
			will-change: width;
		}
		.SpectrogramPlayer-timelineAfter {
			/*@noflip*/
			right: 0;
			/*@noflip*/
			left: auto;
		}
	}

	// error message
	.SpectrogramPlayer-errorMessage {
		@include centerCenter;
		@include inset-md;
		color: $color-neutral-5;
		background-color: $color-overlay-white;
	}

	// spinner
	.LoadingAnimation {
		@include centerCenter;
		background-color: transparent;
		pointer-events: none;
	}
}

// size variants
.SpectrogramPlayer--sm,
.SpectrogramPlayer--compact {
	.SpectrogramPlayer-spect,
	.SpectrogramPlayer-poster {
		height: 168px;
	}
	.SpectrogramPlayer-frame {
		height: 184px;
	}
}

.SpectrogramPlayer--xs {
	.SpectrogramPlayer-spect,
	.SpectrogramPlayer-poster {
		height: 110px;
	}
	.SpectrogramPlayer-frame {
		height: 126px;
	}
	.SpectrogramPlayer-xAxis,
	.SpectrogramPlayer-yAxis {
		font-size: 10px;
		.SpectrogramPlayer-tick {
			padding-top: 0px;
		}
		.SpectrogramPlayer-khz {
			bottom: -1px;
		}
	}
}

// inline variants
$poster-width: 640px;
$poster-width-sm: 489px;
$poster-width-xs: 320px;

.SpectrogramPlayer--inline {
	max-width: $poster-width;
	&.SpectrogramPlayer--compact,
	&.SpectrogramPlayer--sm {
		max-width: $poster-width-sm;
	}
	&.SpectrogramPlayer--xs {
		max-width: $poster-width-xs;
	}
}

// color variants
.SpectrogramPlayer--primary {
	$spect-primary-dark: darken($color-primary-5, 5%);
	.MediaControls-controls {
		background-color: $spect-primary-dark;
	}
	.MediaControls-branding {
		display: none;
	}
	.MediaControls-volume .MediaControls-volumeSliderOverlay {
		background-color: $spect-primary-dark;
	}
	.MediaControls-meta {
		a {
			color: $color-link-reverse;
		}
	}
}

// add border under x axis
.SpectrogramPlayer--annotation {
	// the x axis
	.SpectrogramPlayer-xAxis {
		border-bottom: 1px solid $color-neutral-1;
		.SpectrogramPlayer-tick {
			&:nth-child(4n + 1) {
				background-color: transparent;
			}
		}
	}
}

// Playlist component uses this variant to show asset metadata in the control bar
.SpectrogramPlayer--withMeta {
	.MediaControls-meta {
		.MediaControls-title {
			//@include h5;
			@include stack-xs;
			&:empty {
				display: none;
			}
		}
	}
	@supports (grid-area: x) {
		.MediaControls-controls {
			@include inset-squish-sm;
			display: grid;
			grid-template-areas:
				'play volume meta'
				'time blank meta';
			grid-gap: $space-sm;
			grid-template-columns: auto auto 1fr;
			grid-template-rows: auto 1fr;
		}
		.MediaControls-playButton {
			grid-area: play;
		}
		.MediaControls-time {
			grid-area: time;
			align-self: start;
			@include inset-squish-sm;
		}
		.MediaControls-volume {
			grid-area: volume;
		}
		.MediaControls-meta {
			grid-area: meta;
			@include inset-sm;
		}
	}
}
</style>
